import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

/**
 * Check if an execution plan exists for a specific test case
 * @param {Object} params - Parameters
 * @param {string} params.testCaseId - Test case ID to check for execution plan
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function checkExecutionPlanExists({ testCaseId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCaseId) {
      return {
        status: STATUS.ERROR,
        error: "Test case ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/checkExecutionPlanExists`,
      {
        params: { testCaseId },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error checking execution plan:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get execution results for a specific test launch
 * @param {Object} params - Parameters
 * @param {string} params.launchId - Test launch ID
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Page size (default: 25)
 * @param {string} params.executionMode - Execution mode filter (optional)
 * @param {Object} params.filter - Filter object (optional)
 * @param {Object} params.addOnFilters - Additional filters (optional)
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function getExecutionResult({ launchId, page = 1, pageSize = 25, executionMode, filter, addOnFilters } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!launchId) {
      return {
        status: STATUS.ERROR,
        error: "Launch ID is required",
      };
    }

    const params = {
      page,
      pageSize,
    };

    if (executionMode) {
      params.executionMode = executionMode;
    }

    if (filter) {
      params.filter = filter;
    }

    if (addOnFilters) {
      params.addOnFilters = addOnFilters;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/getExecutionResult/${launchId}`,
      {
        params,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || { rows: [], pagination: {} },
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching execution results:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update execution result status
 * @param {Object} params - Parameters
 * @param {Array<string>} params.executionResultIds - Array of execution result IDs
 * @param {string} params.statusToBeUpdated - New status to set
 * @param {string} params.testLaunchId - Test launch ID
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function updateExecutionResultStatus({ executionResultIds, statusToBeUpdated, testLaunchId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!executionResultIds || !Array.isArray(executionResultIds) || executionResultIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Execution result IDs are required",
      };
    }

    if (!statusToBeUpdated) {
      return {
        status: STATUS.ERROR,
        error: "Status to be updated is required",
      };
    }

    if (!testLaunchId) {
      return {
        status: STATUS.ERROR,
        error: "Test launch ID is required",
      };
    }

    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/updateExecutionResultStatus`,
      {
        executionResultIds,
        statusToBeUpdated,
        testLaunchId,
      },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating execution result status:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Store test execution result
 * @param {Object} params - Parameters
 * @param {Object} params.execution - Execution details
 * @param {Object} params.summary - Execution summary (optional)
 * @param {Array} params.executionSteps - Execution steps
 * @param {Object} params.testPlan - Test plan (optional)
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function storeTestExecutionResult({ execution, summary, executionSteps, testPlan } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!execution) {
      return {
        status: STATUS.ERROR,
        error: "Execution details are required",
      };
    }

    if (!executionSteps) {
      return {
        status: STATUS.ERROR,
        error: "Execution steps are required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/test-executions`,
      {
        execution,
        summary,
        executionSteps,
        testPlan,
      },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error storing test execution result:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get execution record by test case ID
 * @param {Object} params - Parameters
 * @param {string} params.testCaseId - Test case ID
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function getExecutionRecordByTestcaseId({ testCaseId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCaseId) {
      return {
        status: STATUS.ERROR,
        error: "Test case ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/execution-records/${testCaseId}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching execution record:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Export automation plan (instructions + plan) for a test case from the server.
 * @param {Object} params - Parameters
 * @param {string} params.testCaseId - Test case ID
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function exportAutomationPlan({ testCaseId } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: 'Project ID is required',
      };
    }

    if (!testCaseId) {
      return {
        status: STATUS.ERROR,
        error: 'Test case ID is required',
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/export-plan/${testCaseId}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error || 'Export failed',
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error('Error exporting automation plan:', error);
    return extractErrorInfo(error);
  }
}

/**
 * Get variable values via execution plan
 * @param {Object} params - Parameters
 * @param {string} params.testcaseId - Test case ID
 * @param {string} params.environmentId - Environment ID (optional)
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function getVariableValueViaPlan({ testcaseId, environmentId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testcaseId) {
      return {
        status: STATUS.ERROR,
        error: "Test case ID is required",
      };
    }

    const params = { testcaseId };
    
    if (environmentId) {
      params.environmentId = environmentId;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/getVariableValueViaPlan`,
      {
        params,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching variable values:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get automation instruction for a test case
 * @param {Object} params - Parameters
 * @param {string} params.id - Test case ID
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function getAutomationInstruction({ id } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!id) {
      return {
        status: STATUS.ERROR,
        error: "Test case ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/getAutoMationInstruction/${id}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching automation instruction:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Check if automation instruction exists
 * @param {Object} params - Parameters
 * @param {string} params.id - Test case ID
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function checkAutomationInstruction({ id } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!id) {
      return {
        status: STATUS.ERROR,
        error: "Test case ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/checkAutomationInstruction/${id}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error checking automation instruction:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get execution record (raw format)
 * @param {Object} params - Parameters
 * @param {string} params.id - Test case ID
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function getExecutionRecord({ id } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!id) {
      return {
        status: STATUS.ERROR,
        error: "Test case ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/getExecutionRecord/${id}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching execution record:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update execution record
 * @param {Object} params - Parameters
 * @param {string} params.testCaseId - Test case ID
 * @param {Object} params.plan - Execution plan with steps
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function updateExecutionRecord({ testCaseId, plan, dynamicResult } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCaseId) {
      return {
        status: STATUS.ERROR,
        error: "Test case ID is required",
      };
    }

    if (!plan) {
      return {
        status: STATUS.ERROR,
        error: "Execution plan is required",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/updateExecutionRecord`,
      {
        testCaseId,
        plan,
        dynamicResult,
      },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating execution record:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update automation instruction
 * @param {Object} params - Parameters
 * @param {string} params.testCaseId - Test case ID
 * @param {string} params.automationInstruction - Automation instruction text
 * @returns {Promise<Object>} Response object with status, data, and message
 */
export async function updateAutomationInstruction({ testCaseId, automationInstruction } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCaseId) {
      return {
        status: STATUS.ERROR,
        error: "Test case ID is required",
      };
    }

    if (!automationInstruction || automationInstruction.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Automation instruction is required",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/updateAutomationInstruction`,
      {
        testCaseId,
        automationInstruction,
      },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating automation instruction:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Save automation instructions only and remove persisted execution plan.
 * @param {Object} params
 * @param {string} params.testCaseId
 * @param {Array} params.automationInstruction
 * @returns {Promise<Object>}
 */
export async function saveAutomationInstructionOnly({ testCaseId, automationInstruction } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCaseId) {
      return {
        status: STATUS.ERROR,
        error: "Test case ID is required",
      };
    }

    if (!automationInstruction || automationInstruction.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Automation instruction is required",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/auto-execution/save-instruction-only`,
      {
        testCaseId,
        automationInstruction,
      },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error saving automation instruction only:", error);
    return extractErrorInfo(error);
  }
}

